import React from 'react'
import { Box, Container, Typography, Stack } from '@mui/material'
import Grid from '@mui/material/Grid2'
import {
  Event as EventIcon,
  Gavel as GavelIcon,
  School as SchoolIcon,
  Handshake as HandshakeIcon,
  FamilyRestroom as FamilyRestroomIcon,
} from '@mui/icons-material'

/**
 * Program allocation configuration
 */
export interface ProgramAllocation {
  /**
   * Program name
   */
  name: string

  /**
   * Program description
   */
  description: string

  /**
   * Material-UI icon name (optional)
   */
  icon?: 'Event' | 'Gavel' | 'School' | 'Handshake' | 'FamilyRestroom'
}

export interface FundAllocationSectionProps {
  /**
   * Section heading
   */
  title: string

  /**
   * Introduction paragraph
   */
  intro: string

  /**
   * Array of program allocations
   */
  programs: ProgramAllocation[]

  /**
   * Optional conclusion text
   */
  conclusion?: string
}

// Icon component mapping
const iconComponents = {
  Event: EventIcon,
  Gavel: GavelIcon,
  School: SchoolIcon,
  Handshake: HandshakeIcon,
  FamilyRestroom: FamilyRestroomIcon,
}

/**
 * Fund Allocation Section Component
 *
 * Displays transparent breakdown of how donations are allocated across
 * NCESP programs and initiatives. Optimized for desktop viewing with
 * responsive grid layout.
 *
 * @example
 * ```tsx
 * <FundAllocationSection
 *   title="Where Your Donation Goes"
 *   intro="Your contribution helps NCESP..."
 *   programs={fundAllocationPrograms}
 *   conclusion="Every contribution makes a difference"
 * />
 * ```
 */
export const FundAllocationSection: React.FC<FundAllocationSectionProps> = ({
  title,
  intro,
  programs,
  conclusion,
}) => {
  return (
    <Box
      component="section"
      sx={{
        py: { xs: 6, sm: 8, md: 10 },
        bgcolor: 'grey.50',
      }}
      aria-labelledby="fund-allocation-title"
    >
      <Container maxWidth="lg">
        <Stack spacing={{ xs: 4, sm: 5, md: 6 }}>
          {/* Section Header */}
          <Box
            sx={{
              textAlign: 'center',
              maxWidth: '800px',
              mx: 'auto !important',
            }}
          >
            <Typography
              id="fund-allocation-title"
              variant="h2"
              component="h2"
              sx={{
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                fontWeight: 700,
                color: 'text.primary',
                mb: 2,
              }}
            >
              {title}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1rem', sm: '1.0625rem', md: '1.125rem' },
                color: 'text.secondary',
                lineHeight: 1.8,
              }}
            >
              {intro}
            </Typography>
          </Box>

          {/* Programs Grid */}
          <Grid
            container
            spacing={{ xs: 3, sm: 4 }}
            sx={{ justifyContent: 'center' }}
          >
            {programs.map(program => {
              const IconComponent = program.icon
                ? iconComponents[program.icon]
                : null

              return (
                <Grid key={program.name} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      p: { xs: 3, sm: 4 },
                      bgcolor: 'background.paper',
                      borderRadius: 2,
                      boxShadow: 1,
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        boxShadow: 3,
                        transform: 'translateY(-4px)',
                      },
                    }}
                  >
                    {/* Program Icon */}
                    {IconComponent && (
                      <Box
                        sx={{
                          mb: 2,
                          color: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <IconComponent
                          sx={{
                            fontSize: { xs: 48, sm: 56, md: 64 },
                          }}
                          aria-hidden="true"
                        />
                      </Box>
                    )}

                    {/* Program Name */}
                    <Typography
                      variant="h3"
                      component="h3"
                      sx={{
                        fontSize: {
                          xs: '1.25rem',
                          sm: '1.375rem',
                          md: '1.5rem',
                        },
                        fontWeight: 600,
                        color: 'text.primary',
                        mb: 1.5,
                      }}
                    >
                      {program.name}
                    </Typography>

                    {/* Program Description */}
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: {
                          xs: '0.875rem',
                          sm: '0.9375rem',
                          md: '1rem',
                        },
                        color: 'text.secondary',
                        lineHeight: 1.7,
                      }}
                    >
                      {program.description}
                    </Typography>
                  </Box>
                </Grid>
              )
            })}
          </Grid>

          {/* Conclusion */}
          {conclusion && (
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1rem', sm: '1.0625rem', md: '1.125rem' },
                color: 'text.secondary',
                textAlign: 'center',
                mx: 'auto',
                fontStyle: 'italic',
              }}
            >
              {conclusion}
            </Typography>
          )}
        </Stack>
      </Container>
    </Box>
  )
}
